import React, { useState, useEffect, useCallback, useRef, useContext, createContext } from 'react';
import baseApi from '../api/axios';
import { fmtIstDate } from '../utils/dateUtils';
import { inr, qty, SummaryCard, Pager, ORDER_STATUS_COLORS } from './brandstoreAdminShared';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';

// eYantra's view of a company's gift coupons — same features as the store admin's Vouchers tab
// in brandstore-frontend (pages/VouchersTab.jsx, which this is a port of), for one company at a
// time. Uses the same tenant-scoped endpoints (BrandstoreVoucherController), which let a central
// ADMIN into any store. Only companies onboarded with "Enable Voucher Management?" = Yes appear.

// Each component reads the selected company's API from here, so the ported code below can keep
// calling api.get('/admin/vouchers…') unchanged.
const ApiCtx = createContext(null);
const useVoucherApi = () => useContext(ApiCtx);

const fmtDate = (d) => fmtIstDate(d, { day: '2-digit', month: 'short', year: 'numeric' });
const todayLocal = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const input = { padding: '8px 10px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none', color: '#111827', background: '#fff', boxSizing: 'border-box' };
const fieldLabel = { fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4, display: 'block' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1.5px solid #E5E7EB', whiteSpace: 'nowrap' };
const thNum = { ...th, textAlign: 'right' };
const td = { padding: '10px 12px', fontSize: 13, color: '#111827', borderBottom: '1px solid #F3F4F6', verticalAlign: 'top' };
const tdNum = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
const btnPrimary = { padding: '8px 16px', borderRadius: 7, border: 'none', background: 'var(--color-primary,#F29213)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const btnGhost = { padding: '8px 16px', borderRadius: 7, border: '1.5px solid #E5E7EB', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const btnLink = { background: 'none', border: 'none', padding: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer' };


// Same order-status colours and coupon highlight as the Orders views.

function OrderStatusBadge({ status }) {
  const c = ORDER_STATUS_COLORS[status] || { bg: '#f5f5f5', color: '#555' };
  return (
    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>
      {String(status || '').replace(/_/g, ' ')}
    </span>
  );
}

export function CouponChip({ code }) {
  return (
    <span title="Coupon used on this order" style={{ display: 'inline-block', marginTop: 4, background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', borderRadius: 6, padding: '1px 7px', fontSize: 11, fontWeight: 800, letterSpacing: 0.3, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'nowrap' }}>
      🎟️ {code}
    </span>
  );
}

const PAGE_SIZE = 20;
const CODES_PAGE_SIZE = 10;
const FILTERS = [
  { key: 'ALL',         label: 'All' },
  { key: 'ACTIVE',      label: 'Active' },
  { key: 'FULLY_USED',  label: 'Fully used' },
  { key: 'EXPIRED',     label: 'Expired' },
  { key: 'DEACTIVATED', label: 'Deactivated' },
];
const STATUS_STYLE = {
  ACTIVE:      { bg: '#DCFCE7', fg: '#15803D', label: 'Active' },
  FULLY_USED:  { bg: '#DBEAFE', fg: '#1D4ED8', label: 'Fully used' },
  EXPIRED:     { bg: '#FEF3C7', fg: '#B45309', label: 'Expired' },
  DEACTIVATED: { bg: '#F3F4F6', fg: '#6B7280', label: 'Deactivated' },
  USED:        { bg: '#DCFCE7', fg: '#15803D', label: 'Used' },
  IN_CHECKOUT: { bg: '#EDE9FE', fg: '#6D28D9', label: 'Paying now' },
};
const BATCH_TONE = { bg: '#F5F3FF', fg: '#6D28D9', border: '#DDD6FE' };
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
const muted = { fontSize: 12, color: '#6B7280' };
const errorOf = (err, fallback) => err.response?.data?.error || fallback;
const plural = (n, word) => `${qty(n)} ${word}${n === 1 ? '' : 's'}`;

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.DEACTIVATED;
  return <span style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

/** "12 / 250" with a fill bar. */
function UsageBar({ used, max, noun = 'uses' }) {
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const full = used >= max;
  return (
    <div style={{ minWidth: 130 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
        {qty(used)} <span style={{ color: '#9CA3AF', fontWeight: 500 }}>/ {qty(max)}</span>
      </div>
      <div style={{ height: 5, background: '#F3F4F6', borderRadius: 3, marginTop: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: full ? '#1D4ED8' : 'var(--color-primary,#F29213)' }} />
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{full ? `No ${noun} left` : `${qty(max - used)} ${noun} left`}</div>
    </div>
  );
}

function ModalShell({ title, subtitle, onClose, children, width = 480 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: width, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#111827' }}>{title}</h3>
            {subtitle && <div style={{ ...muted, marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 18, color: '#9CA3AF', cursor: 'pointer' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CopyCode({ code, big }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ ...mono, fontWeight: 700, fontSize: big ? 18 : 13, letterSpacing: big ? 1 : 0.3, color: '#111827' }}>{code}</span>
      <button type="button" style={{ ...btnLink, color: 'var(--color-primary,#F29213)', fontSize: 11 }} onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
    </span>
  );
}

/** "⋯" button with a dropdown. Positioned fixed so the table's horizontal scroll doesn't clip it. */
function ActionMenu({ items, label = '⋯' }) {
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  useEffect(() => {
    if (!pos) return undefined;
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setPos(null); };
    const dismiss = () => setPos(null);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [pos]);
  const toggle = () => {
    if (pos) { setPos(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  };
  return (
    <span ref={wrapRef} style={{ display: 'inline-block' }}>
      <button ref={btnRef} type="button" onClick={toggle} aria-label={label === '⋯' ? 'More actions' : undefined} aria-expanded={!!pos} style={{
        border: '1.5px solid #E5E7EB', background: pos ? '#F3F4F6' : '#fff', borderRadius: 7,
        padding: label === '⋯' ? '3px 9px' : '8px 14px',
        fontSize: label === '⋯' ? 15 : 13, fontWeight: label === '⋯' ? 700 : 600, color: '#374151', cursor: 'pointer', lineHeight: 1.3,
      }}>{label}</button>
      {pos && (
        <div role="menu" style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 900, background: '#fff', borderRadius: 9, boxShadow: '0 8px 28px rgba(0,0,0,0.16)', border: '1px solid #F3F4F6', padding: 4, minWidth: 200, textAlign: 'left' }}>
          {items.filter(Boolean).map(it => (
            <button key={it.label} role="menuitem" type="button"
              onClick={() => { setPos(null); it.onClick(); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', color: it.danger ? '#B91C1C' : it.good ? '#15803D' : '#111827' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >{it.label}</button>
          ))}
        </div>
      )}
    </span>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function downloadCodes(api, notify, batchId) {
  try {
    const res = await api.get('/admin/vouchers/export-codes', { params: batchId ? { batchId } : {}, responseType: 'blob' });
    downloadBlob(res.data, batchId ? `coupon-codes-${batchId}.csv` : `coupon-codes-${todayLocal()}.csv`);
  } catch {
    notify('Download failed. Please try again.', 'error');
  }
}

async function downloadUsage(api, notify) {
  try {
    const res = await api.get('/admin/vouchers/export', { responseType: 'blob' });
    downloadBlob(res.data, `coupon-usage-${todayLocal()}.csv`);
  } catch {
    notify('Export failed. Please try again.', 'error');
  }
}

const batchTitle = (b) => b.batchName || 'Single-use codes';

// ── Create a coupon ───────────────────────────────────────────
const COUPON_TYPES = [
  {
    key: 'SHARED',
    icon: '🎟️',
    title: 'One coupon, used multiple times',
    desc: 'One code for everyone (e.g. DIWALI3000). If you allow 10 uses, it works 10 times only — once per employee.',
  },
  {
    key: 'SINGLE_USE',
    icon: '📦',
    title: 'Multiple coupons, single use each',
    // Each code is limited, not each person — one employee holding two codes can use both.
    desc: 'Generates as many codes as you choose. Each code works for one order only. The limit is per code, not per person — someone with two codes can use both.',
  },
];

function TypeChoice({ value, onChange }) {
  return (
    <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
      {COUPON_TYPES.map(t => {
        const on = value === t.key;
        return (
          <label key={t.key} style={{
            display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', padding: '10px 12px', borderRadius: 8,
            border: on ? '1.5px solid var(--color-primary,#F29213)' : '1.5px solid #E5E7EB', background: on ? '#F9FAFB' : '#fff',
          }}>
            <input type="radio" name="couponType" checked={on} onChange={() => onChange(t.key)} style={{ marginTop: 3, accentColor: 'var(--color-primary,#F29213)' }} />
            <span style={{ fontSize: 18, lineHeight: 1.2 }} aria-hidden>{t.icon}</span>
            <span>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#111827' }}>{t.title}</span>
              <span style={{ display: 'block', fontSize: 12, color: '#6B7280', marginTop: 2 }}>{t.desc}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

function NewCouponModal({ onClose, onCreated }) {
  const api = useVoucherApi();
  const { addToast } = useToast();
  const [form, setForm] = useState({ type: 'SHARED', code: '', prefix: '', batchName: '', valueInr: '3000', maxUses: '', quantity: '', expiryDate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const shared = form.type === 'SHARED';

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const body = shared
      ? { type: 'SHARED', code: form.code, maxUses: Number(form.maxUses) }
      : { type: 'SINGLE_USE', prefix: form.prefix, batchName: form.batchName, quantity: Number(form.quantity) };
    try {
      const res = await api.post('/admin/vouchers', { ...body, valueInr: Number(form.valueInr), expiryDate: form.expiryDate });
      setCreated(res.data);
      onCreated();
    } catch (err) {
      setError(errorOf(err, 'Could not create the coupon.'));
    } finally {
      setSaving(false);
    }
  };

  if (created?.type === 'SINGLE_USE') {
    const preview = created.codes.slice(0, 8);
    return (
      <ModalShell title={`${plural(created.quantity, 'code')} generated`} subtitle={<>{created.batchName || 'Single-use codes'} · batch <span style={mono}>{created.batchId}</span></>} onClose={onClose}>
        <div style={{ background: BATCH_TONE.bg, border: `1.5px dashed ${BATCH_TONE.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'grid', gap: 4 }}>
          {preview.map(c => <CopyCode key={c} code={c} />)}
          {created.codes.length > preview.length && (
            <span style={muted}>…and {qty(created.codes.length - preview.length)} more in the download</span>
          )}
        </div>
        <p style={{ fontSize: 13, color: '#374151', margin: '0 0 18px' }}>
          {inr(created.valueInr)} off each · <strong>every code works once</strong> · valid until {fmtDate(created.expiryDate)}.
          Download the codes and give one to each employee — they aren't emailed automatically.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={btnGhost} onClick={() => downloadCodes(api, addToast, created.batchId)}>⬇ Download codes</button>
          <button style={btnPrimary} onClick={onClose}>Done</button>
        </div>
      </ModalShell>
    );
  }

  if (created) {
    return (
      <ModalShell title="Coupon created" onClose={onClose}>
        <div style={{ background: '#F9FAFB', border: '1.5px dashed #D1D5DB', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
          <CopyCode code={created.code} big />
        </div>
        <p style={{ fontSize: 13, color: '#374151', margin: '0 0 18px' }}>
          {inr(created.valueInr)} off · can be used <strong>{qty(created.maxUses)}</strong> time{created.maxUses === 1 ? '' : 's'} in total, once per employee · valid until {fmtDate(created.expiryDate)}.
          Share the code with your employees — it isn't emailed automatically.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button style={btnPrimary} onClick={onClose}>Done</button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell title="New coupon" onClose={onClose} width={520}>
      <form onSubmit={submit}>
        <label style={fieldLabel}>How should the coupon be used?</label>
        <TypeChoice value={form.type} onChange={v => set('type', v)} />

        {shared ? (
          <div style={{ marginBottom: 12 }}>
            <label style={fieldLabel}>Coupon code</label>
            <input
              style={{ ...input, ...mono, width: '100%', textTransform: 'uppercase' }}
              value={form.code} maxLength={30}
              onChange={e => set('code', e.target.value.toUpperCase().replace(/\s+/g, ''))}
              placeholder="e.g. DIWALI3000 — leave blank to generate one"
            />
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 200px' }}>
              <label style={fieldLabel}>Batch name (optional)</label>
              <input style={{ ...input, width: '100%' }} value={form.batchName} maxLength={80}
                onChange={e => set('batchName', e.target.value)} placeholder="e.g. Diwali 2026 – Hyderabad team" />
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label style={fieldLabel}>Code prefix (optional)</label>
              <input
                style={{ ...input, ...mono, width: '100%', textTransform: 'uppercase' }}
                value={form.prefix} maxLength={12}
                onChange={e => set('prefix', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                placeholder="e.g. DIWALI"
              />
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={fieldLabel}>Value (₹){shared ? '' : ' per code'}</label>
            <input style={{ ...input, width: '100%' }} type="number" min="1" step="1" required value={form.valueInr} onChange={e => set('valueInr', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            {shared ? (<>
              <label style={fieldLabel}>Number of uses</label>
              <input style={{ ...input, width: '100%' }} type="number" min="1" step="1" required value={form.maxUses} onChange={e => set('maxUses', e.target.value)} placeholder="e.g. 10" />
            </>) : (<>
              <label style={fieldLabel}>Number of codes</label>
              <input style={{ ...input, width: '100%' }} type="number" min="1" max="5000" step="1" required value={form.quantity} onChange={e => set('quantity', e.target.value)} placeholder="e.g. 250" />
            </>)}
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabel}>Expiry date</label>
          <input style={{ ...input, width: '100%' }} type="date" min={todayLocal()} required value={form.expiryDate} onChange={e => set('expiryDate', e.target.value)} />
        </div>
        <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '9px 12px', fontSize: 12, color: '#4B5563', margin: '0 0 14px' }}>
          {shared
            ? <>The code stops working after {form.maxUses ? qty(Number(form.maxUses)) : 'the set number of'} uses in total. Each employee can use it only once.</>
            : <>Codes look like <span style={mono}>{form.prefix}7KQ2MX9P</span>. Each one works exactly once — give one to each employee.</>}
          {' '}Coupons apply to orders worth at least their value; anything above it is paid by card/UPI.
        </div>
        {error && <p style={{ color: '#B91C1C', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={btnGhost} onClick={onClose}>Cancel</button>
          <button type="submit" style={btnPrimary} disabled={saving}>
            {saving ? 'Creating…' : shared ? 'Create coupon' : `Generate ${form.quantity ? plural(Number(form.quantity), 'code') : 'codes'}`}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ── Edit one coupon (expiry; number of uses for shared codes) ──
function EditCouponModal({ coupon, onClose, onSaved }) {
  const api = useVoucherApi();
  const [expiryDate, setExpiryDate] = useState(coupon.expiryDate);
  const [maxUses, setMaxUses] = useState(String(coupon.maxUses));
  const singleUse = coupon.type === 'SINGLE_USE';
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      if (!singleUse && Number(maxUses) !== coupon.maxUses) await api.put(`/admin/vouchers/${coupon.id}/max-uses`, { maxUses: Number(maxUses) });
      if (expiryDate !== coupon.expiryDate) await api.put(`/admin/vouchers/${coupon.id}/expiry`, { expiryDate });
      onSaved();
      onClose();
    } catch (err) {
      setError(errorOf(err, 'Could not save the changes.'));
      onSaved(); // the first of the two calls may have gone through
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={<>Edit <span style={mono}>{coupon.code}</span></>} onClose={onClose} width={420}>
      <form onSubmit={save}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
          {!singleUse && <div style={{ flex: 1 }}>
            <label style={fieldLabel}>Number of uses</label>
            <input style={{ ...input, width: '100%' }} type="number" min={Math.max(1, coupon.usesCount)} step="1" required value={maxUses} onChange={e => setMaxUses(e.target.value)} />
          </div>}
          <div style={{ flex: 1 }}>
            <label style={fieldLabel}>Expiry date</label>
            {/* min only once changed — an already-expired coupon must still save other edits. */}
            <input style={{ ...input, width: '100%' }} type="date" min={expiryDate !== coupon.expiryDate ? todayLocal() : undefined} required value={expiryDate} onChange={e => setExpiryDate(e.target.value)} />
          </div>
        </div>
        <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 14px' }}>
          {singleUse
            ? 'Single-use code — it always works once. To change every code in the batch, use the batch menu.'
            : <>Already used {qty(coupon.usesCount)} time{coupon.usesCount === 1 ? '' : 's'} — the limit can't go below that.</>}
        </p>
        {error && <p style={{ color: '#B91C1C', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={btnGhost} onClick={onClose}>Cancel</button>
          <button type="submit" style={btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </ModalShell>
  );
}

// ── Edit a whole batch (name + expiry) ────────────────────────
function EditBatchModal({ batch, onClose, onSaved }) {
  const api = useVoucherApi();
  const [batchName, setBatchName] = useState(batch.batchName || '');
  const [expiryDate, setExpiryDate] = useState(batch.expiryDate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mixedExpiry = batch.expiryMinDate && batch.expiryMinDate !== batch.expiryDate;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      if (batchName.trim() !== (batch.batchName || '')) await api.put(`/admin/vouchers/batches/${batch.batchId}/name`, { batchName });
      if (expiryDate !== batch.expiryDate) await api.put(`/admin/vouchers/batches/${batch.batchId}/expiry`, { expiryDate });
      onSaved();
      onClose();
    } catch (err) {
      setError(errorOf(err, 'Could not save the changes.'));
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Edit batch" subtitle={<>Applies to all {plural(batch.codes, 'code')} in <span style={mono}>{batch.batchId}</span></>} onClose={onClose} width={440}>
      <form onSubmit={save}>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabel}>Batch name</label>
          <input style={{ ...input, width: '100%' }} value={batchName} maxLength={80} onChange={e => setBatchName(e.target.value)} placeholder="e.g. Diwali 2026 – Hyderabad team" />
        </div>
        <div style={{ marginBottom: 6 }}>
          <label style={fieldLabel}>Expiry date (every code)</label>
          {/* Only validated and sent when changed, so an expired batch can still be renamed. */}
          <input style={{ ...input, width: '100%' }} type="date" min={expiryDate !== batch.expiryDate ? todayLocal() : undefined} required value={expiryDate} onChange={e => setExpiryDate(e.target.value)} />
        </div>
        <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 14px' }}>
          {mixedExpiry
            ? `Codes in this batch currently expire between ${fmtDate(batch.expiryMinDate)} and ${fmtDate(batch.expiryDate)} — pick a new date to set them all to it.`
            : 'Extending the date brings expired, unused codes back into use.'}
        </p>
        {error && <p style={{ color: '#B91C1C', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={btnGhost} onClick={onClose}>Cancel</button>
          <button type="submit" style={btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </ModalShell>
  );
}

// ── Who used a coupon ─────────────────────────────────────────
const DELIVERY_LABELS = { HOME: '🏠 Home', OFFICE: '🏢 Office' };
const variantOf = (o) => [o.selectedSize, o.selectedColor].filter(Boolean).join('/');

/** One use of the coupon, laid out like an order row in the admin Orders tab — same coupon
 *  chip, payment label and status colours. */
function UsageRow({ r, code }) {
  const orders = r.orders || [];
  const paidOnTop = Number(r.paidInr) > 0;
  return (
    <tr style={{ background: '#FFFBEB', boxShadow: 'inset 3px 0 0 #FCD34D' }}>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        <div style={{ fontWeight: 700, color: '#111827' }}>{r.orderIds?.length ? `#${r.orderIds.join(', #')}` : '—'}</div>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{fmtDate(r.redeemedAt || r.createdAt)}</div>
      </td>
      <td style={{ ...td, fontSize: 12 }}>{r.employeeEmail}</td>
      <td style={{ ...td, fontSize: 12, maxWidth: 220 }}>
        {orders.length === 0 ? <span style={{ color: '#9CA3AF' }}>—</span> : orders.map(o => (
          <div key={o.id} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: orders.length === 1 ? 600 : 500, color: '#374151' }}>
            {orders.length > 1 ? '· ' : ''}{o.productName}{variantOf(o) && <span style={{ opacity: 0.6 }}> ({variantOf(o)})</span>}
          </div>
        ))}
        {r.deliveryType && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{DELIVERY_LABELS[r.deliveryType] || r.deliveryType}</div>}
      </td>
      <td style={{ ...td, fontSize: 12, whiteSpace: 'nowrap' }}>
        {paidOnTop ? '🎟️+💳 Coupon' : '🎟️ Coupon'}
        <div><CouponChip code={code} /></div>
      </td>
      <td style={{ ...td, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
        {inr(r.couponAppliedInr)} coupon
        {paidOnTop && <div style={{ fontWeight: 500, color: '#6B7280' }}>+ {inr(r.paidInr)} card/UPI</div>}
      </td>
      <td style={td}>
        {r.status === 'IN_CHECKOUT'
          ? <StatusBadge status="IN_CHECKOUT" />
          : r.orderStatus ? <OrderStatusBadge status={r.orderStatus} /> : <StatusBadge status={r.status} />}
      </td>
    </tr>
  );
}

function UsageModal({ coupon, onClose, onViewOrders }) {
  const api = useVoucherApi();
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setError('');
    api.get(`/admin/vouchers/${coupon.id}/redemptions`, { params: { page, size: PAGE_SIZE } })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(err => { if (!cancelled) setError(errorOf(err, 'Failed to load.')); });
    return () => { cancelled = true; };
  }, [coupon.id, page]);

  const rows = data?.content || [];
  return (
    <ModalShell title={<>Orders with <CouponChip code={coupon.code} /></>}
      subtitle={`Used ${qty(coupon.usesCount)} of ${qty(coupon.maxUses)} time${coupon.maxUses === 1 ? '' : 's'} · cancelled orders give their use back and aren't listed`}
      onClose={onClose} width={900}>
      <div style={{ border: '1px solid #F3F4F6', borderRadius: 8, overflow: 'hidden' }}>
        {error ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#B91C1C' }}>{error}</div>
        ) : !data ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#9CA3AF' }}>Nobody has used this coupon yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead><tr>
                <th style={th}>Order</th>
                <th style={th}>Employee</th>
                <th style={th}>Products</th>
                <th style={th}>Payment</th>
                <th style={th}>Amount</th>
                <th style={th}>Status</th>
              </tr></thead>
              <tbody>
                {rows.map(r => <UsageRow key={r.id} r={r} code={coupon.code} />)}
              </tbody>
            </table>
          </div>
        )}
        {data && <Pager page={data.page || 0} totalPages={data.totalPages || 0} totalElements={data.totalElements || 0} onPage={setPage} noun="uses" />}
      </div>
      {onViewOrders && rows.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <button style={btnGhost} onClick={() => { onClose(); onViewOrders(coupon.code); }}>Open in Orders tab →</button>
        </div>
      )}
    </ModalShell>
  );
}

// ── A batch's codes (expanded under its row) ──────────────────
function BatchCodes({ batch, reloadKey, onUsage, onEdit, toggleActive, onViewOrders }) {
  const api = useVoucherApi();
  const [filter, setFilter] = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Page resets in the same render as the new search, so the old page is never fetched with it.
    const id = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);
  useEffect(() => {
    let cancelled = false;
    setError('');
    api.get('/admin/vouchers', { params: { batchId: batch.batchId, filter, search, page, size: CODES_PAGE_SIZE } })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(err => { if (!cancelled) setError(errorOf(err, 'Failed to load codes.')); });
    return () => { cancelled = true; };
  }, [batch.batchId, filter, search, page, reloadKey]);

  const rows = data?.content || [];
  const chip = (f) => (
    <button key={f.key} type="button" onClick={() => { setFilter(f.key); setPage(0); }} style={{
      padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
      border: filter === f.key ? `1.5px solid ${BATCH_TONE.fg}` : '1.5px solid #E5E7EB',
      background: filter === f.key ? BATCH_TONE.fg : '#fff', color: filter === f.key ? '#fff' : '#374151',
      fontWeight: filter === f.key ? 700 : 500,
    }}>{f.label}</button>
  );
  const innerTd = { ...td, padding: '8px 12px', background: '#fff' };

  return (
    <div style={{ background: BATCH_TONE.bg, borderTop: `1px solid ${BATCH_TONE.border}`, borderBottom: `1px solid ${BATCH_TONE.border}`, padding: '12px 16px 14px 46px' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>{FILTERS.map(chip)}</div>
        <input style={{ ...input, padding: '6px 10px', fontSize: 12, flex: '0 1 220px', minWidth: 160 }}
          value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Find a code…" />
      </div>
      <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${BATCH_TONE.border}`, overflow: 'hidden' }}>
        {error ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#B91C1C', fontSize: 13 }}>{error}</div>
        ) : !data ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading codes…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No codes match.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Code</th>
              <th style={th}>Status</th>
              <th style={th}>Used by</th>
              <th style={th}>Expiry</th>
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.id}>
                  <td style={{ ...innerTd, whiteSpace: 'nowrap' }}><CopyCode code={c.code} /></td>
                  <td style={innerTd}><StatusBadge status={c.status} /></td>
                  <td style={{ ...innerTd, fontSize: 12 }}>
                    {c.usedBy?.length
                      ? <button type="button" style={{ ...btnLink, color: 'var(--color-primary,#F29213)', fontWeight: 600 }} onClick={() => onUsage(c)}>{c.usedBy.join(', ')}</button>
                      : <span style={{ color: '#9CA3AF' }}>Not used yet</span>}
                  </td>
                  <td style={{ ...innerTd, whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(c.expiryDate)}</td>
                  <td style={{ ...innerTd, textAlign: 'right' }}>
                    <ActionMenu items={[
                      c.usesCount > 0 && { label: 'View order', onClick: () => onUsage(c) },
                      c.usesCount > 0 && onViewOrders && { label: 'Open in Orders tab', onClick: () => onViewOrders(c.code) },
                      { label: 'Change expiry', onClick: () => onEdit(c) },
                      c.status === 'DEACTIVATED'
                        ? { label: 'Activate code', good: true, onClick: () => toggleActive(c) }
                        : { label: 'Deactivate code', danger: true, onClick: () => toggleActive(c) },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && !error && (
          <Pager page={data.page || 0} totalPages={data.totalPages || 0} totalElements={data.totalElements || 0} onPage={setPage} noun="codes" />
        )}
      </div>
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────
/** onViewOrders(code): optional — switches to the Orders tab filtered to that coupon. */
function VouchersPanel({ onViewOrders }) {
  const api = useVoucherApi();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [filter, setFilter]           = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(0);
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [modal, setModal]             = useState(null); // 'new' | { edit } | { editBatch } | { usage }
  const [expanded, setExpanded]       = useState(() => new Set());
  const [reloadKey, setReloadKey]     = useState(0);
  const reload = useCallback(() => setReloadKey(k => k + 1), []);

  useEffect(() => {
    // Page resets in the same render as the new search, so the old page is never fetched with it.
    const id = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);


  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    api.get('/admin/vouchers/groups', { params: { filter, search, page, size: PAGE_SIZE } })
      .then(r => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(errorOf(err, 'Failed to load coupons.')); setLoading(false); } });
    return () => { cancelled = true; };
  }, [filter, search, page, reloadKey]);

  const toggleExpanded = (key) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const toggleActive = async (c) => {
    const activate = c.status === 'DEACTIVATED';
    if (!activate && !await confirm({
      title: `Deactivate ${c.code}?`,
      message: "Employees won't be able to use it any more. Orders already placed with it aren't affected.",
      confirmLabel: 'Deactivate', tone: 'danger',
    })) return;
    try {
      await api.put(`/admin/vouchers/${c.id}/${activate ? 'activate' : 'deactivate'}`);
      reload();
    } catch (err) {
      addToast(errorOf(err, 'Could not update the coupon.'), 'error');
    }
  };

  const toggleBatch = async (b, activate) => {
    const ok = await confirm(activate
      ? { title: `Activate the ${plural(b.deactivatedCodes, 'switched-off code')} in "${batchTitle(b)}"?`, confirmLabel: 'Activate' }
      : { title: `Deactivate all ${plural(b.codes, 'code')} in "${batchTitle(b)}"?`,
          message: "Unused codes stop working. Orders already placed aren't affected.", confirmLabel: 'Deactivate', tone: 'danger' });
    if (!ok) return;
    try {
      await api.put(`/admin/vouchers/batches/${b.batchId}/${activate ? 'activate' : 'deactivate'}`);
      reload();
    } catch (err) {
      addToast(errorOf(err, 'Could not update the batch.'), 'error');
    }
  };

  const summary = data?.summary || {};
  const rows = data?.content || [];
  const filterBtn = (f) => (
    <button key={f.key} onClick={() => { setFilter(f.key); setPage(0); }} style={{
      padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12,
      background: filter === f.key ? 'var(--color-primary,#F29213)' : '#fff',
      color: filter === f.key ? '#fff' : '#374151',
      fontWeight: filter === f.key ? 700 : 500,
      boxShadow: filter === f.key ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
    }}>{f.label}</button>
  );

  const sharedRow = (c) => (
    <tr key={c.key}>
      <td style={td}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ width: 14, flexShrink: 0 }} />
          <span aria-hidden style={{ fontSize: 18, lineHeight: 1.1 }}>🎟️</span>
          <div>
            <CopyCode code={c.code} />
            <div style={{ ...muted, fontSize: 11, marginTop: 3 }}>Shared code · created {fmtDate(c.createdAt)}</div>
          </div>
        </div>
      </td>
      <td style={tdNum}>{inr(c.valueInr)}</td>
      <td style={td}><UsageBar used={c.usesCount} max={c.maxUses} /></td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(c.expiryDate)}</td>
      <td style={td}><StatusBadge status={c.status} /></td>
      <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
        <button style={{ ...btnLink, color: 'var(--color-primary,#F29213)', marginRight: 12 }} onClick={() => setModal({ usage: c })}>
          Who used ({qty(c.usesCount)})
        </button>
        <ActionMenu items={[
          c.usesCount > 0 && onViewOrders && { label: 'Open in Orders tab', onClick: () => onViewOrders(c.code) },
          { label: 'Edit uses & expiry', onClick: () => setModal({ edit: c }) },
          c.status === 'DEACTIVATED'
            ? { label: 'Activate', good: true, onClick: () => toggleActive(c) }
            : { label: 'Deactivate', danger: true, onClick: () => toggleActive(c) },
        ]} />
      </td>
    </tr>
  );

  const batchRow = (b) => {
    const open = expanded.has(b.key);
    const mixedExpiry = b.expiryMinDate && b.expiryMinDate !== b.expiryDate;
    const mixedValue = b.valueMinInr != null && Number(b.valueMinInr) !== Number(b.valueInr);
    return (
      <React.Fragment key={b.key}>
        <tr style={{ background: open ? BATCH_TONE.bg : undefined, cursor: 'pointer' }} onClick={() => toggleExpanded(b.key)}>
          <td style={td}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span aria-hidden style={{ width: 14, flexShrink: 0, color: BATCH_TONE.fg, fontSize: 10, paddingTop: 4, transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none' }}>▶</span>
              <span aria-hidden style={{ fontSize: 18, lineHeight: 1.1 }}>📦</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{batchTitle(b)}</div>
                <div style={{ ...muted, fontSize: 11, marginTop: 4 }}>
                  <span style={{ background: '#fff', color: BATCH_TONE.fg, border: `1px solid ${BATCH_TONE.border}`, borderRadius: 10, padding: '1px 7px', fontWeight: 700, marginRight: 6 }}>
                    {plural(b.codes, 'single-use code')}
                  </span>
                  <span style={mono}>{b.batchId}</span> · created {fmtDate(b.createdAt)}
                </div>
              </div>
            </div>
          </td>
          <td style={tdNum}>{mixedValue ? `${inr(b.valueMinInr)}–${inr(b.valueInr)}` : inr(b.valueInr)}<div style={{ ...muted, fontSize: 11 }}>each</div></td>
          <td style={td}><UsageBar used={b.usedCodes} max={b.codes} noun="codes" /></td>
          <td style={{ ...td, whiteSpace: 'nowrap' }}>
            {mixedExpiry ? <>{fmtDate(b.expiryMinDate)}<div style={{ ...muted, fontSize: 11 }}>to {fmtDate(b.expiryDate)}</div></> : fmtDate(b.expiryDate)}
          </td>
          <td style={td}>
            <StatusBadge status={b.status} />
            {b.deactivatedCodes > 0 && b.status !== 'DEACTIVATED' && (
              <div style={{ ...muted, fontSize: 11, marginTop: 4 }}>{qty(b.deactivatedCodes)} switched off</div>
            )}
          </td>
          <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
            <button style={{ ...btnLink, color: BATCH_TONE.fg, marginRight: 12 }} onClick={() => toggleExpanded(b.key)}>
              {open ? 'Hide codes' : 'View codes'}
            </button>
            <ActionMenu items={[
              { label: '⬇ Download codes', onClick: () => downloadCodes(api, addToast, b.batchId) },
              { label: 'Rename / change expiry', onClick: () => setModal({ editBatch: b }) },
              b.deactivatedCodes > 0 && { label: 'Activate switched-off codes', good: true, onClick: () => toggleBatch(b, true) },
              b.status !== 'DEACTIVATED' && { label: 'Deactivate whole batch', danger: true, onClick: () => toggleBatch(b, false) },
            ]} />
          </td>
        </tr>
        {open && (
          <tr>
            <td colSpan={6} style={{ padding: 0 }}>
              <BatchCodes batch={b} reloadKey={reloadKey}
                onUsage={c => setModal({ usage: c })} onEdit={c => setModal({ edit: c })} toggleActive={toggleActive} onViewOrders={onViewOrders} />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 2px' }}>Coupons</h2>
          <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>Shared coupon codes and batches of single-use codes for this company's employees.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <ActionMenu label="⬇ Export ▾" items={[
            { label: 'All coupon codes (CSV)', onClick: () => downloadCodes(api, addToast, '') },
            { label: 'Who used what (CSV)', onClick: () => downloadUsage(api, addToast) },
          ]} />
          <button style={btnPrimary} onClick={() => setModal('new')}>+ New coupon</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <SummaryCard label="Active codes" value={`${qty(summary.active)} / ${qty(summary.coupons)}`} tone="#15803D" />
        <SummaryCard label="Times used" value={qty(summary.redemptions)} tone="#1D4ED8" />
        <SummaryCard label="Value redeemed" value={inr(summary.redeemedValueInr)} />
        <SummaryCard label="Expired" value={qty(summary.expired)} tone={summary.expired ? '#B45309' : undefined} />
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{FILTERS.map(filterBtn)}</div>
        <input
          style={{ ...input, flex: '0 1 280px', minWidth: 180 }}
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search code or batch name…"
        />
      </div>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading && !data ? (
          <div style={{ textAlign: 'center', padding: 50, color: '#9CA3AF' }}>Loading…</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 50, color: '#B91C1C' }}>{error}</div>
        ) : rows.length === 0 ? (
          filter === 'ALL' && !search ? (
            <div style={{ textAlign: 'center', padding: '50px 20px' }}>
              <div style={{ fontSize: 30 }} aria-hidden>🎟️</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '8px 0 4px' }}>No coupons yet</div>
              <div style={{ ...muted, marginBottom: 14 }}>Create a shared code, or generate single-use codes for each employee.</div>
              <button style={btnPrimary} onClick={() => setModal('new')}>+ New coupon</button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 50, color: '#9CA3AF' }}>No coupons match these filters.</div>
          )
        ) : (
          <div style={{ overflowX: 'auto', opacity: loading ? 0.6 : 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead><tr>
                <th style={th}>Coupon</th>
                <th style={thNum}>Value</th>
                <th style={th}>Usage</th>
                <th style={th}>Expiry</th>
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr></thead>
              <tbody>
                {rows.map(r => (r.kind === 'BATCH' ? batchRow(r) : sharedRow(r)))}
              </tbody>
            </table>
          </div>
        )}
        {data && !error && (
          <Pager page={data.page || 0} totalPages={data.totalPages || 0} totalElements={data.totalElements || 0} onPage={setPage} noun="coupons & batches" />
        )}
      </div>

      {modal === 'new' && <NewCouponModal onClose={() => setModal(null)} onCreated={reload} />}
      {modal?.edit && <EditCouponModal coupon={modal.edit} onClose={() => setModal(null)} onSaved={reload} />}
      {modal?.editBatch && <EditBatchModal batch={modal.editBatch} onClose={() => setModal(null)} onSaved={reload} />}
      {modal?.usage && <UsageModal coupon={modal.usage} onClose={() => setModal(null)} onViewOrders={onViewOrders} />}
    </div>
  );
}

export default function BrandstoreVouchersTab({ tenants }) {
  const enabledTenants = tenants.filter(t => t.voucherManagementEnabled && t.active !== false);
  const [slug, setSlug] = useState('');
  const selected = enabledTenants.find(t => t.slug === slug) || enabledTenants[0];
  const tenantSlug = selected?.slug;
  const api = React.useMemo(() => tenantSlug && ({
    get:  (url, config)       => baseApi.get(`/brandstore/${tenantSlug}${url}`, config),
    post: (url, body, config) => baseApi.post(`/brandstore/${tenantSlug}${url}`, body, config),
    put:  (url, body, config) => baseApi.put(`/brandstore/${tenantSlug}${url}`, body, config),
  }), [tenantSlug]);

  if (enabledTenants.length === 0) {
    return (
      <div style={{ background: '#fff', borderRadius: 10, padding: 40, textAlign: 'center', color: '#6B7280', fontSize: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        No company has vouchers enabled yet. Turn on "Enable Voucher Management?" for a company in the Companies tab.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={fieldLabel}>Company</label>
          <select style={{ ...input, minWidth: 220 }} value={tenantSlug} onChange={e => setSlug(e.target.value)}>
            {enabledTenants.map(t => <option key={t.id} value={t.slug}>{t.displayName}</option>)}
          </select>
        </div>
      </div>
      <ApiCtx.Provider value={api}>
        {/* keyed by company so filters/pages/modals reset when switching */}
        <VouchersPanel key={tenantSlug} />
      </ApiCtx.Provider>
    </div>
  );
}
